import dayjs from "dayjs"
import { useCallback, useEffect, useMemo, useState } from "react"
import {
  CartesianGrid,
  Cell,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis
} from "recharts"

import { LoadingSpinner } from "@/components/shared"
import { Card, Tabs, TabsList, TabsTrigger } from "@/components/ui"
import { cn } from "@/modules/helpers/hooks/shared/utils"
import { TikTokTrendingDataPoint } from "@/modules/types/users/tiktok"

// Color palettes for charts
const GENDER_COLORS = ["#4D9DFF", "#FF6B9D", "#B8C5D0"]
const LOCATION_COLORS = ["#0B5F6B", "#0E8B9F", "#7AC6D6", "#B8D9E0", "#D4E8ED"]
const AGE_COLORS = ["#0B5F6B", "#0E8B9F", "#7AC6D6", "#B8D9E0"]

type TimeFilter = "today" | "7days" | "30days" | "365days" | "custom"
type MetricTab = "followers" | "gmv" | "sales" | "views" | "engagement"

type DemographicData = {
  name?: string
  key?: string
  range?: string
  value: number | string
}

type TikTokAnalyticsProps = {
  trendingData: TikTokTrendingDataPoint[]
  followerGender?: DemographicData[]
  followerStateLocation?: DemographicData[]
  followerAges?: DemographicData[]
  isLoading?: boolean
  onTimeFilterChange?: (startTime: string, endTime: string) => void
}

export function TikTokAnalytics({
  trendingData,
  followerGender = [],
  followerStateLocation = [],
  followerAges = [],
  isLoading = false,
  onTimeFilterChange
}: TikTokAnalyticsProps) {
  const [activeTab, setActiveTab] = useState<MetricTab>("gmv")
  const [timeFilter, setTimeFilter] = useState<TimeFilter>("30days")

  const timeFilterOptions: { value: TimeFilter; label: string }[] = [
    // { value: "today", label: "Hôm nay" },
    { value: "7days", label: "7 ngày" },
    { value: "30days", label: "30 ngày" }
    // { value: "365days", label: "365 ngày" },
    // { value: "custom", label: "Tùy chỉnh" }
  ]

  // Calculate date range based on filter
  const getDateRange = useCallback((filter: TimeFilter) => {
    const endDate = dayjs()
    let startDate = dayjs()

    switch (filter) {
      case "today":
        startDate = dayjs()
        break
      case "7days":
        startDate = dayjs().subtract(7, "day")
        break
      case "30days":
        startDate = dayjs().subtract(30, "day")
        break
      case "365days":
        startDate = dayjs().subtract(365, "day")
        break
      case "custom":
        // For custom, keep current range
        return null
      default:
        startDate = dayjs().subtract(7, "day")
    }

    return {
      startTime: startDate.format("YYYY-MM-DD"),
      endTime: endDate.format("YYYY-MM-DD")
    }
  }, [])

  // Handle time filter change
  const handleTimeFilterChange = useCallback(
    (filter: TimeFilter) => {
      setTimeFilter(filter)
      const dateRange = getDateRange(filter)
      if (dateRange && onTimeFilterChange) {
        onTimeFilterChange(dateRange.startTime, dateRange.endTime)
      }
    },
    [getDateRange, onTimeFilterChange]
  )

  // Call onTimeFilterChange on mount with default timeFilter (30days)
  useEffect(() => {
    const dateRange = getDateRange(timeFilter)
    if (dateRange && onTimeFilterChange) {
      onTimeFilterChange(dateRange.startTime, dateRange.endTime)
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const formatNumber = (num: number) => {
    if (num >= 1000000000) {
      return `${(num / 1000000000).toFixed(num % 1000000000 === 0 ? 0 : 1)}B`
    }
    if (num >= 1000000) {
      return `${(num / 1000000).toFixed(num % 1000000 === 0 ? 0 : 1)}M`
    }
    if (num >= 1000) {
      return `${(num / 1000).toFixed(num % 1000 === 0 ? 0 : 1)}K`
    }
    return num.toString()
  }

  // Get value based on active tab
  const getValueByTab = useCallback(
    (item: TikTokTrendingDataPoint) => {
      switch (activeTab) {
        case "followers":
          return item.follower
        case "gmv":
          return item.gmv
        case "sales":
          return item.unitsSold
        case "views":
          return item.videoPlayCnt
        case "engagement":
          return item.videoEngagementRate
        default:
          return 0
      }
    },
    [activeTab]
  )

  // Process trending data based on active tab
  const processedChartData = useMemo(() => {
    if (!trendingData || !Array.isArray(trendingData)) return []

    // Sort by date first (oldest to newest)
    const sortedData = [...trendingData].sort(
      (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    )

    return sortedData.map((item) => {
      const date = dayjs(item.createdAt)
      const day = date.format("DD/MM")

      return {
        day,
        date: item.createdAt,
        value: getValueByTab(item)
      }
    })
  }, [trendingData, getValueByTab])

  // Add colors to demographic data
  const genderDataWithColors = useMemo(() => {
    const genderNames = {
      male: "Nam",
      female: "Nữ"
    }
    if (!followerGender || followerGender.length === 0) return []

    const total = followerGender.reduce(
      (sum, item) => sum + (typeof item.value === "string" ? parseFloat(item.value) : item.value),
      0
    )

    // Parse raw values
    const parsed = followerGender.map((item) => {
      const raw = typeof item.value === "string" ? parseFloat(item.value) : (item.value as number)
      const numeric = (raw / total) * 100

      return {
        ...item,
        name: genderNames[item.key as keyof typeof genderNames],
        numeric
      }
    })

    const totalNumeric = parsed.reduce((sum, item) => sum + item.numeric, 0)

    const allFractions =
      parsed.length > 0 &&
      parsed.every((item) => item.numeric >= 0 && item.numeric <= 1) &&
      totalNumeric <= 1 + 1e-6

    const result = parsed.map((item) => {
      let percentage: number

      if (allFractions) {
        // Dữ liệu dạng 0.x => coi như tỷ lệ
        percentage = item.numeric * 100
      } else if (totalNumeric > 0) {
        // Dữ liệu là số tuyệt đối => quy đổi sang %
        percentage = (item.numeric / totalNumeric) * 100
      } else {
        percentage = 0
      }

      return {
        ...item,
        value: item.numeric,
        percentage
      }
    })

    // Nếu là dữ liệu dạng fraction và tổng < 1 thì thêm phần "Chưa xác định"
    if (allFractions) {
      const remainingFraction = Math.max(0, 1 - totalNumeric)
      const remainingPercentage = remainingFraction * 100

      if (remainingPercentage > 0.01) {
        result.push({
          key: "unknown",
          name: "Chưa xác định",
          numeric: remainingFraction,
          value: remainingFraction,
          percentage: remainingPercentage
        } as any)
      }
    }

    return result.map((item, index) => ({
      ...item,
      color: GENDER_COLORS[index % GENDER_COLORS.length]
    }))
  }, [followerGender])

  const locationDataWithColors = useMemo(
    () =>
      (followerStateLocation || []).map((item, index) => ({
        ...item,
        name: item.name || item.key,
        value: typeof item.value === "string" ? parseInt(item.value) : item.value,
        color: LOCATION_COLORS[index % LOCATION_COLORS.length]
      })),
    [followerStateLocation]
  )

  const ageDataWithColors = useMemo(
    () =>
      (followerAges || []).map((item, index) => {
        const value = typeof item.value === "string" ? parseFloat(item.value) : item.value
        return {
          ...item,
          range: item.range || item.key,
          value: value,
          percentage: value < 1 ? value * 100 : value, // Convert to percentage if it's decimal
          color: AGE_COLORS[index % AGE_COLORS.length]
        }
      }),
    [followerAges]
  )

  const maxValue = useMemo(() => {
    if (!processedChartData || processedChartData.length === 0) return 0
    const values = processedChartData.map((d) => d.value).filter((v) => typeof v === "number")
    return values.length > 0 ? Math.max(...values) : 0
  }, [processedChartData])

  // Calculate summary stats
  const summaryStats = useMemo(() => {
    if (!processedChartData || processedChartData.length === 0) {
      return { change: 0, total: 0 }
    }

    // Get latest (newest) and earliest (oldest) values
    const latestValue = processedChartData[processedChartData.length - 1]?.value || 0
    const earliestValue = processedChartData[0]?.value || 0

    // Calculate change
    const change = latestValue - earliestValue

    return {
      change,
      total: latestValue
    }
  }, [processedChartData])

  if (isLoading) {
    return (
      <div className="w-full pb-6">
        <div className="flex items-center justify-center py-12">
          <LoadingSpinner className="w-20 h-20" />/
        </div>
      </div>
    )
  }

  return (
    <div className="w-full pb-6">
      {/* Tabs for different metrics */}
      <Tabs
        value={activeTab}
        onValueChange={(v) => setActiveTab(v as MetricTab)}
        className="w-full"
      >
        <div className="overflow-x-auto scrollbar-hide">
          <TabsList className="w-full min-w-max bg-transparent h-auto p-0 gap-4 mb-4">
            <TabsTrigger
              value="gmv"
              className="data-[state=active]:bg-white data-[state=active]:!rounded-t-md !shadow-none data-[state=active]:border-b-2 border-0 !border-b data-[state=active]:border-primary-800 rounded-none px-2 py-2 text-xs text-black data-[state=active]:text-primary-800"
            >
              GMV
            </TabsTrigger>
            <TabsTrigger
              value="followers"
              className="data-[state=active]:bg-white data-[state=active]:!rounded-t-md !shadow-none data-[state=active]:border-b-2 border-0 !border-b data-[state=active]:border-primary-800 rounded-none px-2 py-2 text-xs text-black data-[state=active]:text-primary-800"
            >
              Người theo dõi
            </TabsTrigger>
            <TabsTrigger
              value="sales"
              className="data-[state=active]:bg-white data-[state=active]:!rounded-t-md !shadow-none data-[state=active]:border-b-2 border-0 !border-b data-[state=active]:border-primary-800 rounded-none px-2 py-2 text-xs text-black data-[state=active]:text-primary-800"
            >
              Bán ra
            </TabsTrigger>
            <TabsTrigger
              value="views"
              className="data-[state=active]:bg-white data-[state=active]:!rounded-t-md !shadow-none data-[state=active]:border-b-2 border-0 !border-b data-[state=active]:border-primary rounded-none px-2 py-2 text-xs text-black"
            >
              Lượt xem video
            </TabsTrigger>
            <TabsTrigger
              value="engagement"
              className="data-[state=active]:bg-white data-[state=active]:!rounded-t-md !shadow-none data-[state=active]:border-b-2 border-0 !border-b data-[state=active]:border-primary rounded-none px-2 py-2 text-xs text-black"
            >
              Tỷ lệ tương tác
            </TabsTrigger>
          </TabsList>
        </div>

        {/* Time Filter Pills */}
        <div className="flex gap-2 mb-4 overflow-x-auto scrollbar-hide">
          {timeFilterOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleTimeFilterChange(option.value)}
              disabled={option.value === "custom"}
              className={cn(
                "px-3 py-1 rounded-full text-xs whitespace-nowrap transition-colors",
                timeFilter === option.value
                  ? "bg-white text-primary-800"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-200",
                option.value === "custom" && "opacity-50 cursor-not-allowed"
              )}
            >
              {option.label}
            </button>
          ))}
        </div>

        {/* Single analytics component for all tabs */}
        <div className="mt-0">
          <AnalyticsContent
            lineChartData={processedChartData}
            genderData={genderDataWithColors}
            locationData={locationDataWithColors}
            ageData={ageDataWithColors}
            maxValue={maxValue}
            formatNumber={formatNumber}
            summaryStats={summaryStats}
          />
        </div>
      </Tabs>
    </div>
  )
}

type AnalyticsContentProps = {
  lineChartData: { day: string; date?: string; value: number }[]
  genderData: { name?: string; value: number; percentage?: number; color: string }[]
  locationData: { name?: string; value: number; color: string }[]
  ageData: { range?: string; value: number; percentage?: number; color: string }[]
  maxValue: number
  formatNumber: (num: number) => string
  summaryStats: { change: number; total: number }
}

function AnalyticsContent({
  lineChartData,
  genderData,
  locationData,
  ageData,
  maxValue,
  formatNumber
}: AnalyticsContentProps) {
  const dateRange = useMemo(() => {
    if (lineChartData.length < 2) return ""
    const startDate = dayjs(lineChartData[0]?.date)
    const endDate = dayjs(lineChartData[lineChartData.length - 1]?.date)
    return `${startDate.format("DD/MM/YYYY")} - ${endDate.format("DD/MM/YYYY")}`
  }, [lineChartData])

  // Calculate max age percentage for bar width
  const maxAgePercentage = useMemo(() => {
    if (!ageData || ageData.length === 0) return 100
    return Math.max(...ageData.map((d) => d.percentage || d.value))
  }, [ageData])

  // Calculate chart width for horizontal scroll when data is many (30 days)
  const chartWidth = useMemo(() => {
    const minWidthPerPoint = 50 // Minimum width per data point in pixels
    const dataPoints = lineChartData.length
    const calculatedWidth = dataPoints * minWidthPerPoint
    // Return width in pixels if > 100% (need scroll), otherwise return "100%"
    return calculatedWidth
  }, [lineChartData.length])

  // Calculate Y-axis ticks based on maxValue
  const yAxisTicks = useMemo(() => {
    if (maxValue === 0) return [0]

    // Calculate a nice rounded max value for the domain
    // Add 10-20% padding to maxValue for better visualization
    const padding = Math.max(maxValue * 0.15, 10)
    const domainMax = Math.ceil(maxValue + padding)

    // Calculate step to get 4-5 nice ticks
    const step = Math.ceil(domainMax / 4)

    // Round step to nice numbers (1, 2, 5, 10, 20, 50, 100, etc.)
    const magnitude = Math.pow(10, Math.floor(Math.log10(step)))
    const normalized = step / magnitude
    let niceStep = magnitude
    if (normalized <= 1) niceStep = magnitude
    else if (normalized <= 2) niceStep = 2 * magnitude
    else if (normalized <= 5) niceStep = 5 * magnitude
    else niceStep = 10 * magnitude

    // Round domainMax up to the next nice number
    const niceDomainMax = Math.ceil(domainMax / niceStep) * niceStep

    // Generate ticks: 0, step, 2*step, ... up to niceDomainMax
    const ticks: number[] = [0]
    let current = niceStep
    while (current <= niceDomainMax) {
      ticks.push(current)
      current += niceStep
    }

    return ticks
  }, [maxValue])
  return (
    <div className="space-y-4">
      {/* Main Metrics Card */}
      <Card className="border-none!shadow-none bg-white">
        <div className="px-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-medium text-gray-700">Số liệu chính</h3>
            <span className="text-xs text-black">{dateRange}</span>
          </div>

          {/* Metrics Summary */}
          {/* <div className="mb-6">
            <div className="flex items-baseline gap-2 mb-1 justify-between">
              <span
                className={`text-xl font-bold ${
                  summaryStats.change >= 0 ? "text-primary" : "text-red-600"
                }`}
              >
                {summaryStats.change >= 0 ? "+" : ""}
                {formatNumber(summaryStats.change)}
              </span>
              <span className="text-sm text-gray-600">
                Tổng{" "}
                <span className="text-primary-800 font-medium">
                  {formatNumber(summaryStats.total)}
                </span>
              </span>
            </div>
            <div className="flex items-center gap-2 text-xs text-black">
              <span>{formatNumber(summaryStats.change)}</span>
            </div>
          </div> */}

          {/* Line Chart */}
          <div className="h-[215px] w-full relative">
            {/* Sticky YAxis - Custom rendered */}
            <div className="absolute -left-1 top-0 bottom-0 z-10 min-w-[30px] h-full flex justify-center bg-white pr-2">
              {yAxisTicks.map((tick, index) => {
                // Get the actual domain max from the ticks array
                const domainMax = yAxisTicks[yAxisTicks.length - 1]
                // Calculate position: 0 at bottom (0%), domainMax at top (100%)
                // The chart has margins: top: 5px, bottom: 5px, so the actual chart area is 205px
                // Position should be calculated based on the chart area, not including margins
                const chartHeight = 215
                const topMargin = 5
                const bottomMargin = 50 // Increased to match chart margin
                const chartAreaHeight = chartHeight - topMargin - bottomMargin
                const percentage = domainMax > 0 ? tick / domainMax : 0
                // Position from bottom of container: bottomMargin + percentage of chart area
                const positionFromBottom = bottomMargin + percentage * chartAreaHeight
                const positionPercent = (positionFromBottom / chartHeight) * 100
                return (
                  <div
                    key={index}
                    className="absolute right-2 text-[10px] text-black"
                    style={{ bottom: `${positionPercent}%`, transform: "translateY(50%)" }}
                  >
                    {formatNumber(tick)}
                  </div>
                )
              })}
            </div>
            {/* Scrollable Chart */}
            <div className="h-full overflow-x-auto px-2">
              <ResponsiveContainer width={chartWidth > 300 ? chartWidth : "100%"} height={215}>
                <LineChart
                  data={lineChartData}
                  margin={{ top: 5, right: 20, left: 40, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                  <YAxis
                    domain={[0, yAxisTicks[yAxisTicks.length - 1] || maxValue + 300]}
                    hide={true}
                  />
                  <XAxis
                    dataKey="day"
                    axisLine={false}
                    tickLine={false}
                    tick={{ fill: "#000", fontSize: 10 }}
                  />
                  <Tooltip
                    formatter={(value) => {
                      const numValue = typeof value === "number" ? value : Number(value) || 0
                      return [formatNumber(numValue), "Giá trị"]
                    }}
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #E5E7EB",
                      borderRadius: "8px",
                      fontSize: "12px"
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="value"
                    stroke="#0091aa"
                    strokeWidth={2}
                    dot={{ fill: "#0091aa", r: 4 }}
                    activeDot={{ r: 6 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </Card>

      {/* Follower Details Card */}
      <Card className="border-none!shadow-none bg-white">
        <div className="px-4">
          <h3 className="text-sm font-medium text-gray-700 mb-4">Chi tiết người theo dõi</h3>

          {!genderData.length && !locationData.length && !ageData.length && (
            <div className="text-center text-xs py-8 text-black">
              <p>Dữ liệu chi tiết người theo dõi đang được cập nhật</p>
            </div>
          )}

          {/* Gender Distribution */}
          {genderData.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-600 mb-3">Giới tính</h4>
              <div className="flex items-center gap-6">
                <div className="w-32 h-32 relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={genderData}
                        cx="50%"
                        cy="50%"
                        innerRadius={35}
                        outerRadius={60}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {genderData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2">
                  {genderData.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <span className="text-sm text-gray-700">{item.name}</span>
                      </div>
                      <span className="text-sm font-medium text-gray-700">
                        {(item.percentage ?? 0).toFixed(2)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Location Distribution */}
          {locationData.length > 0 && (
            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-600 mb-3">Vị trí</h4>
              <div className="flex items-center gap-6">
                <div className="w-32 h-32 relative">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={locationData}
                        cx="50%"
                        cy="50%"
                        innerRadius={35}
                        outerRadius={60}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {locationData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                    </PieChart>
                  </ResponsiveContainer>
                </div>
                <div className="flex-1 space-y-2">
                  {locationData.map((item, index) => (
                    <div key={index} className="flex items-center gap-2 justify-between">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <span className="text-sm text-gray-700">{item.name}</span>
                      </div>
                      <span className="text-sm font-medium text-gray-700">{item.value / 100}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Age Distribution */}
          {ageData.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-gray-600 mb-3">Độ tuổi</h4>
              <div className="space-y-2">
                {ageData.map((item, index) => {
                  const percentage = item.percentage || item.value
                  return (
                    <div key={index} className="flex items-center gap-3">
                      <div className="w-24 text-sm text-gray-700 flex-shrink-0">{item.range}</div>
                      <div className="flex-1 h-8 bg-gray-100 rounded-md overflow-hidden relative">
                        <div
                          className="h-full rounded-md transition-all"
                          style={{
                            width: `${(percentage / maxAgePercentage) * 100}%`,
                            backgroundColor: item.color
                          }}
                        ></div>
                      </div>
                      <span className="text-sm font-medium text-gray-700 w-12 text-right">
                        {percentage.toFixed(1)}%
                      </span>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  )
}
